import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OnboardingFlowDef, OnboardingFlowId, OnboardingStepDef, ONBOARDING_FLOW_CONFIGS } from '@cosmos/shared';
import { driver, Driver } from 'driver.js';
import { firstValueFrom } from 'rxjs';
import { StateService } from './state.service';
import { environment } from '../../../../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class OnboardingEngineService {
  private readonly http = inject(HttpClient);
  private readonly stateService = inject(StateService);
  private readonly translateService = inject(TranslateService);

  private readonly flowCache = new Map<string, OnboardingFlowDef>();
  private activeDriver: Driver | null = null;
  private pendingSegmentId: string | null = null;

  isCompleted(groupId: OnboardingFlowId): boolean {
    const config = ONBOARDING_FLOW_CONFIGS.find(c => c.id === groupId);
    if (!config) return false;
    const completed = this.stateService.getCurrentUser()?.onboardingCompleted ?? {};
    return config.segments.filter(s => !s.optional).every(s => !!completed[s.id]);
  }

  isSegmentCompleted(segmentId: string): boolean {
    return !!this.stateService.getCurrentUser()?.onboardingCompleted?.[segmentId];
  }

  resetFlow(groupId: OnboardingFlowId): void {
    const config = ONBOARDING_FLOW_CONFIGS.find(c => c.id === groupId);
    if (!config) return;
    const user = this.stateService.getCurrentUser();
    if (!user?._id) return;

    const updated = { ...(user.onboardingCompleted ?? {}) };
    config.segments.forEach(s => { updated[s.id] = false; });
    this.stateService.setCurrentUser({ ...user, onboardingCompleted: updated });

    this.http
      .patch(`${environment.API_URL}/users/${user._id}`, { onboardingCompleted: updated })
      .subscribe();
  }

  async startFlow(segmentId: string): Promise<void> {
    if (this.activeDriver?.isActive()) {
      this.pendingSegmentId = segmentId;
      return;
    }
    await this.launchFlow(segmentId);
  }

  private waitForAnchor(element: string, timeoutMs = 3000): Promise<boolean> {
    return new Promise(resolve => {
      if (document.querySelector(element)) { resolve(true); return; }
      const deadline = Date.now() + timeoutMs;
      const poll = () => {
        if (document.querySelector(element)) { resolve(true); return; }
        if (Date.now() >= deadline) { resolve(false); return; }
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
  }

  private nextFrame(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  private async launchFlow(segmentId: string): Promise<void> {
    const flow = await this.getFlow(segmentId);
    if (!flow) return;

    // Wait for the first required (non-skipIfMissing) step's element to be in
    // the DOM before initialising driver.js — avoids race conditions where the
    // flow is triggered before Angular has finished rendering the page.
    const anchor = flow.steps.find(s => !s.skipIfMissing);
    if (anchor) {
      const ready = await this.waitForAnchor(anchor.element);
      if (!ready) return;
      // One additional frame after the anchor appears so that sibling @if
      // blocks in the same change-detection cycle have also been painted.
      await this.nextFrame();
    }

    // Filter steps after the DOM has fully settled.
    const steps = this.filterSteps(flow.steps);
    if (!steps.length) return;

    const driverObj = driver({
      showProgress: true,
      popoverClass: 'custom-popover',
      nextBtnText: this.translateService.instant('onboarding.buttons.next'),
      prevBtnText: this.translateService.instant('onboarding.buttons.prev'),
      doneBtnText: this.translateService.instant('onboarding.buttons.done'),
      steps: steps.map(step => ({
        element: step.element,
        popover: {
          title: this.translateService.instant(step.popover.title),
          description: this.translateService.instant(step.popover.description),
          side: (step.popover.side as any) ?? 'bottom',
          align: (step.popover.align as any) ?? 'start',
        },
      })),
      onDestroyed: () => {
        this.activeDriver = null;
        this.persistCompletion(segmentId);
        if (this.pendingSegmentId) {
          const next = this.pendingSegmentId;
          this.pendingSegmentId = null;
          this.launchFlow(next);
        }
      },
    });

    this.activeDriver = driverObj;
    driverObj.drive();
  }

  private async getFlow(segmentId: string): Promise<OnboardingFlowDef | null> {
    if (this.flowCache.has(segmentId)) {
      return this.flowCache.get(segmentId)!;
    }
    try {
      const response = await firstValueFrom(
        this.http.get<OnboardingFlowDef>(`${environment.API_URL}/onboarding/flows/${segmentId}`),
      );
      if (response) {
        this.flowCache.set(segmentId, response);
        return response;
      }
    } catch {
      // silently skip if segment definition is unavailable
    }
    return null;
  }

  private filterSteps(steps: OnboardingStepDef[]): OnboardingStepDef[] {
    const isMobile = window.innerWidth < 768;
    return steps.filter(s => {
      if (isMobile && s.mobileSkip) return false;
      if (s.skipIfMissing && !document.querySelector(s.element)) return false;
      return true;
    });
  }

  private persistCompletion(segmentId: string): void {
    const user = this.stateService.getCurrentUser();
    if (!user?._id) return;

    const updated = { ...(user.onboardingCompleted ?? {}), [segmentId]: true };
    this.stateService.setCurrentUser({ ...user, onboardingCompleted: updated });

    this.http
      .patch(`${environment.API_URL}/users/${user._id}`, { onboardingCompleted: updated })
      .subscribe();
  }
}
